import React, { useState } from 'react';
import { Alert, Button, Card, Col, Container, Form, Nav, Row, Spinner, Tab, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';
import BackendErrorMessage from '../../components/BackendErrorMessage';
import api from '../../api';

interface MediaResult {
  nome: string;
  cpf: string;
  inscricao: string;
  processo: string;
  url: string;
}

// ---------------------------------------------------------------------------
// Sub-componente reutilizável: formulário de busca + resultados
// ---------------------------------------------------------------------------

interface RecoveryPanelProps {
  endpoint: string;
  labelBotao: string;
  labelResultado: string;
  renderPreview: (url: string) => React.ReactNode;
}

const RecoveryPanel: React.FC<RecoveryPanelProps> = ({
  endpoint,
  labelBotao,
  labelResultado,
  renderPreview,
}) => {
  const [busca, setBusca] = useState('');
  const [tipo, setTipo] = useState<'cpf' | 'inscricao'>('cpf');
  const [chamadoJira, setChamadoJira] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<MediaResult[]>([]);
  const [selectedUrl, setSelectedUrl] = useState<string | null>(null);

  const formatCpf = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 11);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
    if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
  };

  const handleBuscaChange = (value: string) => {
    setBusca(tipo === 'cpf' ? formatCpf(value) : value);
  };

  const handleTipoChange = (novoTipo: 'cpf' | 'inscricao') => {
    setTipo(novoTipo);
    setBusca('');
    setResults([]);
    setSelectedUrl(null);
  };

  const handleBuscar = async (e: React.FormEvent) => {
    e.preventDefault();
    setResults([]);
    setSelectedUrl(null);

    if (!busca.trim()) {
      toast.error('Informe o valor de busca.');
      return;
    }
    if (!chamadoJira.trim()) {
      toast.error('Informe o número do chamado Jira.');
      return;
    }
    if (tipo === 'cpf' && busca.replace(/\D/g, '').length !== 11) {
      toast.error('CPF deve conter 11 dígitos.');
      return;
    }

    setLoading(true);
    try {
      const response = await api.get(endpoint, {
        params: {
          busca: tipo === 'cpf' ? busca.replace(/\D/g, '') : busca.trim(),
          tipo,
          chamadoJira: chamadoJira.trim(),
        },
      });
      setResults(response.data);
      toast.success(`${response.data.length} ${labelResultado} encontrado(s).`);
    } catch (error: any) {
      toast.error(BackendErrorMessage(error) || `Erro ao buscar ${labelResultado} do candidato.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Row>
      <Col md={5}>
        <Card>
          <Card.Body>
            <Form onSubmit={handleBuscar}>
              <Form.Group className="mb-3">
                <Form.Label>Buscar por</Form.Label>
                <div>
                  <Form.Check
                    inline type="radio" label="CPF" name={`tipo-${endpoint}`}
                    checked={tipo === 'cpf'} onChange={() => handleTipoChange('cpf')}
                  />
                  <Form.Check
                    inline type="radio" label="Inscrição" name={`tipo-${endpoint}`}
                    checked={tipo === 'inscricao'} onChange={() => handleTipoChange('inscricao')}
                  />
                </div>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>{tipo === 'cpf' ? 'CPF do Candidato' : 'Número de Inscrição'}</Form.Label>
                <Form.Control
                  type="text"
                  placeholder={tipo === 'cpf' ? '000.000.000-00' : 'Ex: 200001'}
                  value={busca}
                  onChange={(e) => handleBuscaChange(e.target.value)}
                  maxLength={tipo === 'cpf' ? 14 : 50}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Chamado Jira</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="PROJ-123 ou URL completa"
                  value={chamadoJira}
                  onChange={(e) => setChamadoJira(e.target.value)}
                  required
                />
                <Form.Text className="text-muted">
                  Ticket (ex: PROJ-123) ou URL do chamado no Jira da UNICAMP.
                </Form.Text>
              </Form.Group>

              <Button type="submit" variant="primary" disabled={loading}>
                {loading ? <Spinner animation="border" size="sm" /> : labelBotao}
              </Button>
            </Form>
          </Card.Body>
        </Card>
      </Col>

      <Col md={7}>
        {results.length > 0 && (
          <Card>
            <Card.Body>
              <Card.Title>Resultados ({results.length})</Card.Title>
              <Table striped hover size="sm" className="mt-2">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>CPF</th>
                    <th>Inscrição</th>
                    <th>Processo</th>
                    <th>Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((r, i) => (
                    <tr key={i}>
                      <td>{r.nome}</td>
                      <td>{r.cpf}</td>
                      <td>{r.inscricao}</td>
                      <td>{r.processo}</td>
                      <td>
                        <Button size="sm" variant="outline-primary" onClick={() => setSelectedUrl(r.url)}>
                          Ver {labelResultado}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        )}

        {selectedUrl && (
          <Card className="mt-3">
            <Card.Body>
              {renderPreview(selectedUrl)}
              <div className="mt-2">
                <Button variant="outline-primary" size="sm" href={selectedUrl} target="_blank" rel="noopener noreferrer">
                  Abrir em nova aba
                </Button>
              </div>
            </Card.Body>
          </Card>
        )}
      </Col>
    </Row>
  );
};

// ---------------------------------------------------------------------------
// Página principal — abas Vídeo e Foto
// ---------------------------------------------------------------------------

const AdminVideoRecoveryPage: React.FC = () => {
  return (
    <Container className="mt-4">
      <h2 className="mb-4">Recuperação de Mídia de Candidato</h2>

      <Alert variant="info" className="mb-4">
        Recupere o vídeo ou a foto de um candidato a partir do CPF ou inscrição.
        É obrigatório informar o número do chamado Jira vinculado à solicitação.
      </Alert>

      <Tab.Container defaultActiveKey="video">
        <Nav variant="tabs" className="mb-3">
          <Nav.Item>
            <Nav.Link eventKey="video">🎥 Vídeo</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="foto">📷 Foto</Nav.Link>
          </Nav.Item>
        </Nav>

        <Tab.Content>
          <Tab.Pane eventKey="video">
            <RecoveryPanel
              endpoint="/admin/videos"
              labelBotao="Buscar Vídeo"
              labelResultado="vídeo"
              renderPreview={(url) => (
                <video
                  src={url}
                  controls
                  style={{ width: '100%', maxHeight: '400px', borderRadius: '8px' }}
                />
              )}
            />
          </Tab.Pane>

          <Tab.Pane eventKey="foto">
            <RecoveryPanel
              endpoint="/admin/fotos"
              labelBotao="Buscar Foto"
              labelResultado="foto"
              renderPreview={(url) => (
                <img
                  src={url}
                  alt="Foto do candidato"
                  style={{ maxWidth: '100%', maxHeight: '400px', borderRadius: '8px', display: 'block' }}
                />
              )}
            />
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
    </Container>
  );
};

export default AdminVideoRecoveryPage;
